import type { PrepaidPolicy, PrepaidOverrides } from '@hue/contracts';
import { Select, HelpTip } from '@hue/design-system';

const fields: Array<{
  key: keyof PrepaidPolicy;
  label: string;
  options: Array<{ value: string; label: string }>;
  hint: string;
}> = [
  {
    key: 'cardRelease',
    label: 'Liberação no cartão',
    options: [
      { value: 'AUTHORIZED', label: 'Na autorização' },
      { value: 'CONFIRMED', label: 'Na confirmação' },
    ],
    hint: 'PIX e dinheiro exigem confirmação. Meios mistos consideram os valores confirmados.',
  },
  {
    key: 'partialRelease',
    label: 'Pagamento parcial',
    options: [
      { value: 'FULL', label: 'Libera só no total' },
      { value: 'PROPORTIONAL', label: 'Libera na proporção' },
    ],
    hint: 'Na proporção, 60% pagos liberam 3 de 5 sessões. Frações aguardam mais pagamento.',
  },
  {
    key: 'validityMonths',
    label: 'Validade',
    options: [0, 3, 6, 12].map((value) => ({
      value: String(value),
      label: value ? `${value} meses` : 'Sem validade',
    })),
    hint: 'Conta a partir da primeira liberação, no fuso da unidade da venda.',
  },
  {
    key: 'extensionMode',
    label: 'Forma de prorrogação',
    options: [
      { value: 'DISABLED', label: 'Não permite' },
      { value: 'CUMULATIVE', label: 'Limite acumulado por saldo' },
      { value: 'PER_REQUEST', label: 'Limite por solicitação' },
    ],
    hint: 'No limite por solicitação, a validade pode ser prorrogada sucessivamente.',
  },
  {
    key: 'extensionMonths',
    label: 'Limite da prorrogação',
    options: Array.from({ length: 25 }, (_, value) => ({
      value: String(value),
      label: `${value} ${value === 1 ? 'mês' : 'meses'}`,
    })),
    hint: 'Quantidade máxima de meses conforme a forma de prorrogação.',
  },
  {
    key: 'noShowConsumes',
    label: 'Falta consome a sessão',
    options: [
      { value: 'true', label: 'Sim' },
      { value: 'false', label: 'Não' },
    ],
    hint: 'Será aplicada quando a Agenda oferecer o estado Faltou.',
  },
  {
    key: 'lateCancellationHours',
    label: 'Cancelamento em cima da hora',
    options: [0, 12, 24, 48].map((value) => ({
      value: String(value),
      label: value ? `Com menos de ${value} horas` : 'Não consome',
    })),
    hint: 'O consumo pelo cancelamento da Agenda será integrado na próxima etapa.',
  },
];
const numeric = new Set(['validityMonths', 'extensionMonths', 'lateCancellationHours']);

export function PolicyEditor({
  value,
  onChange,
  inherited,
}: {
  value: PrepaidOverrides;
  onChange: (value: PrepaidOverrides) => void;
  inherited?: PrepaidPolicy | undefined;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const own = value[field.key];
        const inheritedValue = inherited?.[field.key];
        const inheritedLabel = field.options.find(
          (option) => option.value === String(inheritedValue),
        )?.label;
        return (
          <Select
            key={field.key}
            label={field.label}
            labelAddon={<HelpTip label={`Ajuda: ${field.label}`} content={field.hint} />}
            value={own === undefined ? 'INHERIT' : String(own)}
            options={[
              ...(inherited
                ? [{ value: 'INHERIT', label: `Herdar: ${inheritedLabel ?? 'regra anterior'}` }]
                : []),
              ...field.options,
            ]}
            onValueChange={(next) => {
              const patch = { ...value };
              if (next === 'INHERIT') delete patch[field.key];
              else
                Object.assign(patch, {
                  [field.key]: numeric.has(field.key)
                    ? Number(next)
                    : field.key === 'noShowConsumes'
                      ? next === 'true'
                      : next,
                });
              onChange(patch);
            }}
          />
        );
      })}
    </div>
  );
}
